/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES, type PropertyOverviewSharedStyles } from './propertyOverviewStyles';

/*
 * PropertyOverview.html's property header (.po-head): the photo on the left, and beside it one grid
 * of four columns plus the Edit's - the address with the lead figure on its line, the figures under
 * a hairline, and the facts under another. The figures and the facts are subgrids of that one grid,
 * so every value lands on the same edges.
 */
const WIDE_PHOTO_BREAKPOINT = '@media (max-width: 1180px)';
const STACKED_BREAKPOINT = '@media (max-width: 900px)';

const MONO_KEY = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.textTertiary};
`;

export type PropertyOverviewHeadStyles = {
  shared: PropertyOverviewSharedStyles;
  root: SerializedStyles;
  photo: SerializedStyles;
  /** The right column: four field columns and the Edit's. */
  body: SerializedStyles;
  titleRow: SerializedStyles;
  titleText: SerializedStyles;
  title: SerializedStyles;
  subtitle: SerializedStyles;
  link: SerializedStyles;
  stats: SerializedStyles;
  stat: SerializedStyles;
  /** The one figure on the title's line, at display size. */
  leadStat: SerializedStyles;
  leadStatValue: SerializedStyles;
  statLink: SerializedStyles;
  statValue: SerializedStyles;
  statKey: SerializedStyles;
  /** The facts' band, under its hairline: the facts on four columns, the Edit on the fifth. */
  factsBand: SerializedStyles;
  facts: SerializedStyles;
  fact: SerializedStyles;
  factKey: SerializedStyles;
  factValue: SerializedStyles;
  factUnit: SerializedStyles;
  factInput: SerializedStyles;
  factsActions: SerializedStyles;
  editLink: SerializedStyles;
  actionButton: SerializedStyles;
};

export const DEFAULT_PROPERTY_OVERVIEW_HEAD_STYLES: PropertyOverviewHeadStyles = {
  shared: DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES,
  root: css`
    label: PROPERTY_OVERVIEW_HEAD;
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    column-gap: 28px;
    padding: 18px 20px;
    background: ${COLORS.white};
    border: 1px solid ${COLORS.stroke};

    ${WIDE_PHOTO_BREAKPOINT} {
      grid-template-columns: 220px minmax(0, 1fr);
    }

    ${STACKED_BREAKPOINT} {
      display: block;
    }
  `,
  photo: css`
    label: PROPERTY_OVERVIEW_HEAD_PHOTO;
    position: relative;
    margin: 0;
    min-height: 150px;
    overflow: hidden;
    background-color: ${COLORS.sunken};
    background-size: cover;
    background-position: center;
    border: 1px solid ${COLORS.stroke};

    ${STACKED_BREAKPOINT} {
      aspect-ratio: 4 / 3;
      max-width: 320px;
      min-height: 0;
      margin-bottom: 16px;
    }
  `,
  body: css`
    label: PROPERTY_OVERVIEW_HEAD_BODY;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr)) auto;
    column-gap: 24px;
    align-content: start;
    min-width: 0;

    ${STACKED_BREAKPOINT} {
      grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
    }
  `,
  titleRow: css`
    label: PROPERTY_OVERVIEW_HEAD_TITLE_ROW;
    grid-column: 1 / -1;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 12px;

    ${STACKED_BREAKPOINT} {
      flex-direction: column;
      gap: 10px;
    }
  `,
  titleText: css`
    label: PROPERTY_OVERVIEW_HEAD_TITLE_TEXT;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;
  `,
  title: css`
    label: PROPERTY_OVERVIEW_HEAD_TITLE;
    margin: 0;
    font-family: ${FONT.display};
    font-size: ${pxToRem(20)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: -0.005em;
    line-height: 1.2;
    color: ${COLORS.ink};
    overflow-wrap: anywhere;
  `,
  subtitle: css`
    label: PROPERTY_OVERVIEW_HEAD_SUBTITLE;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13)};
    line-height: 1.35;
    color: ${COLORS.textSecondary};
  `,
  link: css`
    label: PROPERTY_OVERVIEW_HEAD_LINK;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: ${COLORS.ink};
    text-decoration: none;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  `,
  stats: css`
    label: PROPERTY_OVERVIEW_HEAD_STATS;
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    row-gap: 12px;
    padding: 12px 0 14px;
    border-top: 1px solid ${COLORS.stroke};
  `,
  stat: css`
    label: PROPERTY_OVERVIEW_HEAD_STAT;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
    padding: 0;
    margin: 0;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
  `,
  leadStat: css`
    label: PROPERTY_OVERVIEW_HEAD_STAT_LEAD;
    margin-left: auto;
    flex-shrink: 0;
    align-items: flex-end;
    text-align: right;

    ${STACKED_BREAKPOINT} {
      margin-left: 0;
      align-items: flex-start;
      text-align: left;
    }
  `,
  leadStatValue: css`
    label: PROPERTY_OVERVIEW_HEAD_STAT_LEAD_VALUE;
    font-family: ${FONT.display};
    font-size: ${pxToRem(26)};
    letter-spacing: -0.015em;
    line-height: 1.1;
  `,
  statLink: css`
    label: PROPERTY_OVERVIEW_HEAD_STAT_LINK;
    cursor: pointer;

    & > span:first-of-type {
      border-bottom: 1.5px solid ${COLORS.stroke};
      transition: border-color 0.12s;
    }

    &:hover > span:first-of-type {
      border-bottom-color: ${COLORS.ink};
    }

    &:focus-visible {
      outline: 2px solid ${COLORS.ink};
      outline-offset: 3px;
    }
  `,
  statValue: css`
    label: PROPERTY_OVERVIEW_HEAD_STAT_VALUE;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13.5)};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.35;
    color: ${COLORS.ink};
    font-variant-numeric: tabular-nums;
  `,
  statKey: css`
    label: PROPERTY_OVERVIEW_HEAD_STAT_KEY;
    ${MONO_KEY};
  `,
  factsBand: css`
    label: PROPERTY_OVERVIEW_HEAD_FACTS_BAND;
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    align-items: start;
    padding-top: 14px;
    border-top: 1px solid ${COLORS.stroke};
  `,
  facts: css`
    label: PROPERTY_OVERVIEW_HEAD_FACTS;
    grid-column: 1 / 5;
    display: grid;
    grid-template-columns: subgrid;
    row-gap: 14px;
    margin: 0;

    ${STACKED_BREAKPOINT} {
      grid-column: 1 / 3;
    }
  `,
  fact: css`
    label: PROPERTY_OVERVIEW_HEAD_FACT;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  `,
  factKey: css`
    label: PROPERTY_OVERVIEW_HEAD_FACT_KEY;
    ${MONO_KEY};
  `,
  factValue: css`
    label: PROPERTY_OVERVIEW_HEAD_FACT_VALUE;
    margin: 0;
    font-family: ${FONT.body};
    font-size: ${pxToRem(14)};
    line-height: 1.35;
    color: ${COLORS.ink};
    font-variant-numeric: tabular-nums;
  `,
  factUnit: css`
    label: PROPERTY_OVERVIEW_HEAD_FACT_UNIT;
    margin-left: 3px;
    font-size: ${pxToRem(11.5)};
    color: ${COLORS.textTertiary};
    font-variant-numeric: normal;
  `,
  factInput: css`
    label: PROPERTY_OVERVIEW_HEAD_FACT_INPUT;
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    font-family: ${FONT.body};
    font-size: ${pxToRem(14)};
    line-height: 1.4;
    color: ${COLORS.ink};
    outline: none;
    transition: border-color 0.12s, box-shadow 0.12s;

    &:hover {
      border-color: ${COLORS.midtone};
    }

    &:focus {
      border-color: ${COLORS.ink};
      box-shadow: inset 0 0 0 1px ${COLORS.ink};
    }
  `,
  factsActions: css`
    label: PROPERTY_OVERVIEW_HEAD_FACTS_ACTIONS;
    grid-column: 5;
    justify-self: end;
    display: flex;
    gap: 8px;

    ${STACKED_BREAKPOINT} {
      grid-column: 3;
    }
  `,
  /* PropertyOverview.html .po-sec-edit: a quiet, underlined word, not a button's box. */
  editLink: css`
    label: PROPERTY_OVERVIEW_HEAD_EDIT;
    padding: 0;
    border: 0;
    border-bottom: 1.5px solid ${COLORS.stroke};
    background: none;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9.5)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${COLORS.textSecondary};
    cursor: pointer;
    transition: color 0.12s, border-color 0.12s;

    &:hover {
      color: ${COLORS.ink};
      border-bottom-color: ${COLORS.ink};
    }

    &:focus-visible {
      outline: 2px solid ${COLORS.ink};
      outline-offset: 2px;
    }
  `,
  /* PropertyOverview.html .po-btn: Cancel and Save info, smaller than the page's other buttons. */
  actionButton: css`
    label: PROPERTY_OVERVIEW_HEAD_ACTION_BUTTON;
    padding: 6px 12px;
    line-height: 1.2;
  `,
};
